import React from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FarmOrganizationData, farmFormConfig } from '../types/farmTypes';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

interface FarmBasicInfoStepProps {
  formData: FarmOrganizationData;
  handleInputChange: (field: keyof FarmOrganizationData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleSelectChange: (field: keyof FarmOrganizationData) => (
    event: SelectChangeEvent<string> | any
  ) => void;
  handleFacilityChange: (facility: string) => (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const FarmBasicInfoStep: React.FC<FarmBasicInfoStepProps> = ({
  formData,
  handleInputChange,
  handleSelectChange,
  handleFacilityChange,
}) => {
  const { t } = useTranslation('forms');

  return (
    <Stack spacing={3} sx={{ mt: 1 }}>
      {/* Basic Information Group */}
      <Box>
        <Stack spacing={2}>
          {/* Farm Name */}
          <TextField
            label={t('farm.name.label')}
            placeholder={t('farm.name.placeholder')}
            value={formData.name || ''}
            onChange={handleInputChange('name')}
            required={farmFormConfig.name.required}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': { borderColor: DESIGN_TOKENS.colors.organization.farm },
                '&:hover fieldset': { borderColor: DESIGN_TOKENS.colors.organization.farm }
              },
              '& .MuiInputLabel-root.Mui-focused': { color: DESIGN_TOKENS.colors.organization.farm }
            }}
          />

           {/* Area and Capacity */}
           <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
             <TextField
               label={t('farm.area_hectares.label')}
               placeholder={t('farm.area_hectares.placeholder')}
               type="number"
               value={formData.area_hectares || ''}
               onChange={handleInputChange('area_hectares')}
               required={farmFormConfig.area_hectares.required}
               InputProps={{
                 endAdornment: <Typography variant="body2" color="text.secondary">
                   {t('farm.area_hectares.suffix')}
                 </Typography>
               }}
               sx={{
                 '& .MuiOutlinedInput-root': {
                   '&.Mui-focused fieldset': { borderColor: DESIGN_TOKENS.colors.organization.farm },
                   '&:hover fieldset': { borderColor: DESIGN_TOKENS.colors.organization.farm }
                 },
                 '& .MuiInputLabel-root.Mui-focused': { color: DESIGN_TOKENS.colors.organization.farm }
               }}
             />

             <TextField
               label={t('farm.capacity_head.label')}
               placeholder={t('farm.capacity_head.placeholder')}
               type="number"
               value={formData.capacity_head || ''}
               onChange={handleInputChange('capacity_head')}
               required={farmFormConfig.capacity_head.required}
               sx={{
                 '& .MuiOutlinedInput-root': {
                   '&.Mui-focused fieldset': { borderColor: DESIGN_TOKENS.colors.organization.farm },
                   '&:hover fieldset': { borderColor: DESIGN_TOKENS.colors.organization.farm }
                 },
                 '& .MuiInputLabel-root.Mui-focused': { color: DESIGN_TOKENS.colors.organization.farm }
               }}
             />
           </Box>
        </Stack>
      </Box>

       {/* Farm Type and Registration Group */}
       <Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
           <FormControl required>
             <InputLabel>{t('farm.farm_type.label')}</InputLabel>
             <Select
               value={formData.farm_type || ''}
               onChange={handleSelectChange('farm_type')}
               label={t('farm.farm_type.label')}
               sx={{
                 '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                   borderColor: DESIGN_TOKENS.colors.organization.farm 
                 },
                 '&:hover .MuiOutlinedInput-notchedOutline': { 
                   borderColor: DESIGN_TOKENS.colors.organization.farm 
                 }
               }}
             >
               {farmFormConfig.farm_type.options.map(option => (
                 <MenuItem key={option.value} value={option.value}>
                   {t(`farm.farm_type.options.${option.value}`)}
                 </MenuItem>
               ))}
             </Select>
           </FormControl>

           <TextField
             label={t('farm.registration_number.label')}
             placeholder={t('farm.registration_number.placeholder')}
             value={formData.registration_number || ''}
             onChange={handleInputChange('registration_number')}
             required={farmFormConfig.registration_number.required}
             sx={{
               '& .MuiOutlinedInput-root': {
                 '&.Mui-focused fieldset': { borderColor: DESIGN_TOKENS.colors.organization.farm },
                 '&:hover fieldset': { borderColor: DESIGN_TOKENS.colors.organization.farm }
               },
               '& .MuiInputLabel-root.Mui-focused': { color: DESIGN_TOKENS.colors.organization.farm }
             }}
           />
        </Box>
      </Box>

       {/* Production Focus and Country Group */}
       <Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
           <FormControl required sx={{
             '& .MuiInputLabel-root.Mui-focused': { color: DESIGN_TOKENS.colors.primary[700] }
           }}>
             <InputLabel>{t('farm.production_focus.label')}</InputLabel>
             <Select
               value={formData.production_focus || ''}
               onChange={handleSelectChange('production_focus')}
               input={<OutlinedInput label={t('farm.production_focus.label')} />}
               sx={{
                 '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                   borderColor: DESIGN_TOKENS.colors.primary[700]
                 },
                 '&:hover .MuiOutlinedInput-notchedOutline': { 
                   borderColor: DESIGN_TOKENS.colors.primary[700]
                 }
               }}
             >
               {farmFormConfig.production_focus.options.map(option => (
                 <MenuItem key={option.value} value={option.value}>
                   {t(`farm.production_focus.options.${option.value}`)}
                 </MenuItem>
               ))}
             </Select>
           </FormControl>

           <TextField
             label={t('farm.country_code.label')}
             placeholder={t('farm.country_code.placeholder')}
             value={formData.country_code || ''}
             onChange={handleInputChange('country_code')}
             required={farmFormConfig.country_code.required}
             sx={{
               '& .MuiOutlinedInput-root': {
                 '&.Mui-focused fieldset': { borderColor: DESIGN_TOKENS.colors.organization.farm },
                 '&:hover fieldset': { borderColor: DESIGN_TOKENS.colors.organization.farm }
               },
               '& .MuiInputLabel-root.Mui-focused': { color: DESIGN_TOKENS.colors.organization.farm }
             }}
           />
         </Box>
       </Box>

      {/* Facilities Group */}
      <Box>
        <FormGroup>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Array.isArray(formData.facilities) ? formData.facilities.includes('milking_parlor') : false}
                  onChange={handleFacilityChange('milking_parlor')}
                  sx={{
                    color: DESIGN_TOKENS.colors.organization.farm,
                    '&.Mui-checked': { color: DESIGN_TOKENS.colors.organization.farm },
                    '&:hover': { backgroundColor: `${DESIGN_TOKENS.colors.organization.farm}08` }
                  }}
                />
              }
              label={
                <Typography variant="body2" color="text.primary">
                  {t('farm.facilities.milking_parlor')}
                </Typography>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={Array.isArray(formData.facilities) ? formData.facilities.includes('feed_storage') : false}
                  onChange={handleFacilityChange('feed_storage')}
                  sx={{
                    color: DESIGN_TOKENS.colors.organization.farm,
                    '&.Mui-checked': { color: DESIGN_TOKENS.colors.organization.farm },
                    '&:hover': { backgroundColor: `${DESIGN_TOKENS.colors.organization.farm}08` }
                  }}
                />
              }
              label={
                <Typography variant="body2" color="text.primary">
                  {t('farm.facilities.feed_storage')}
                </Typography>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={Array.isArray(formData.facilities) ? formData.facilities.includes('veterinary_facilities') : false}
                  onChange={handleFacilityChange('veterinary_facilities')}
                  sx={{
                    color: DESIGN_TOKENS.colors.organization.farm,
                    '&.Mui-checked': { color: DESIGN_TOKENS.colors.organization.farm },
                    '&:hover': { backgroundColor: `${DESIGN_TOKENS.colors.organization.farm}08` }
                  }}
                />
              }
              label={
                <Typography variant="body2" color="text.primary">
                  {t('farm.facilities.veterinary_facilities')}
                </Typography>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={Array.isArray(formData.facilities) ? formData.facilities.includes('quarantine_area') : false}
                  onChange={handleFacilityChange('quarantine_area')}
                  sx={{
                    color: DESIGN_TOKENS.colors.primary[700],
                    '&.Mui-checked': { color: DESIGN_TOKENS.colors.primary[700] },
                    '&:hover': { backgroundColor: `${DESIGN_TOKENS.colors.primary[700]}08` }
                  }}
                />
              }
              label={
                <Typography variant="body2" color="text.primary">
                  {t('farm.facilities.quarantine_area')}
                </Typography>
              }
            />
          </Box>
        </FormGroup>
      </Box>
    </Stack>
  );
};

export default FarmBasicInfoStep;
